import { EQuizMode, EQuizStatsDateInterval } from '@constants';
import { EQuizStatsDateFilterValue } from '@constants';
import { createQueryString, encodeQueryParams } from '@utils';

import { IPaginatedResponse, IPagination } from '../common';
import { RequestService } from '../request';

import {
  ICreateQuizResult,
  IQuizResult,
  IQuizResultsEvolutionByDateRange,
  IQuizResultFilters,
  IQuizResultsStats,
  IUserQuizProgress,
  IUserQuizStatsByCourse,
  IQuizResultData,
  IQuizResultStatsByCourseSections,
  IUserQuizStatsByCourseSections
} from './quiz-result.contract';

export class QuizResultService extends RequestService {
  constructor(apiUrl: string) {
    super(apiUrl);
  }

  public getLastQuizResultByQuizId(quizId: string) {
    return this.fetcher<IQuizResult>(`/quiz-results/${quizId}`);
  }

  public getQuizResultDataById(quizResultId: string) {
    return this.fetcher<IQuizResultData>(`/quiz-results/details/${quizResultId}`);
  }

  public calculateQuizResults(quizResults: ICreateQuizResult) {
    return this.poster<IQuizResult, ICreateQuizResult>(`/quiz-results/calculate`, quizResults);
  }

  public getUserProgress(dateFilter: EQuizStatsDateFilterValue) {
    const query = createQueryString({
      filters: {
        dateFilter
      }
    });
    return this.fetcher<IUserQuizProgress>(`/quiz-results/stats/users/me/progress?${query}`);
  }

  public getQuizResults(
    pagination: IPagination,
    sort: Record<string, 'asc' | 'desc'>,
    filters: IQuizResultFilters
  ) {
    const query = createQueryString({
      page: pagination.page,
      limit: pagination.limit,
      filters,
      sort:
        Object.keys(sort).length > 0
          ? { sort: Object.keys(sort)[0], order: sort[Object.keys(sort)[0]] }
          : undefined
    });
    return this.fetcher<IPaginatedResponse<IQuizResult>>(`/quiz-results?${query}`);
  }

  public findUserCompletedQuizzesInLast14Days(userId: string) {
    return this.fetcher<IQuizResult[]>(`/quiz-results/last-14-days/${userId}`);
  }

  public canUserRetakeQuiz(quizId: string) {
    return this.fetcher<boolean>(`/quiz-results/can-retake/${quizId}`);
  }

  public findQuizResultsStats(dateFilter: EQuizStatsDateFilterValue) {
    const query = createQueryString({
      filters: {
        dateFilter
      }
    });
    return this.fetcher<IQuizResultsStats>(`/quiz-results/stats/results/users/me?${query}`);
  }

  public findEvolutionByDateRank(
    dateFilter: EQuizStatsDateFilterValue,
    dateInterval: EQuizStatsDateInterval,
    timezone: string
  ) {
    const query = encodeQueryParams({
      dateFilter: dateFilter,
      dateInterval,
      timezone
    });
    return this.fetcher<IQuizResultsEvolutionByDateRange[]>(
      `/quiz-results/evolution/results/users/me?${query}`
    );
  }

  public findUserQuizStatsByCourse(mode?: EQuizMode) {
    const query = createQueryString({
      filters: {
        quizMode: mode ? [mode] : undefined
      }
    });
    return this.fetcher<IUserQuizStatsByCourse[]>(
      `/quiz-results/stats/users/me/quiz-stats-by-course?${query}`
    );
  }

  public findUserQuizStatsByCourseAndFilters(
    filters: IQuizResultFilters,
    sort: Record<string, 'asc' | 'desc'>
  ) {
    const query = createQueryString({
      filters,
      sort: Object.keys(sort).length
        ? { sort: Object.keys(sort)[0], order: sort[Object.keys(sort)[0]] }
        : undefined
    });
    return this.fetcher<IUserQuizStatsByCourseSections[]>(
      `/quiz-results/stats/users/me/quiz-stats-by-course?${query}`
    );
  }

  public findUserQuizStatsByCourseSections(
    courseId: string,
    filters: IQuizResultFilters,
    sort: Record<string, 'asc' | 'desc'>
  ) {
    const query = createQueryString({
      filters,
      sort: Object.keys(sort).length
        ? { sort: Object.keys(sort)[0], order: sort[Object.keys(sort)[0]] }
        : undefined
    });
    return this.fetcher<IQuizResultStatsByCourseSections>(
      `/quiz-results/stats/users/me/quiz-stats-by-course-sections/${courseId}?${query}`
    );
  }

  public getAllQuizResultByQuizMode(modes: EQuizMode[]) {
    const query = createQueryString({
      filters: {
        quizMode: modes
      }
    });
    return this.fetcher<IQuizResult[]>(`/quiz-results/all-by-mode?${query}`);
  }
}
